import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, MapPin, Check } from "lucide-react";
import { AREA_GUIDES, ASSETS, LINKS, ADDRESS, HOURS } from "../lib/constants";
import { useSeo } from "../lib/useSeo";
import { Reveal } from "../components/Reveal";
import { Label, MedicalAccess, MapBlock } from "../components/bits";

export default function AreaGuide() {
  const { slug } = useParams();
  const guide = AREA_GUIDES.find((g) => g.slug === slug) || AREA_GUIDES[0];
  const others = AREA_GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 6);

  useSeo(`/dispensary/${guide.slug}`, {
    title: `Cannabis Dispensary Near ${guide.name} | Higher Ground DC`,
    description: `${guide.blurb} Visit Higher Ground DC at 1135 Okie St NE, Washington, DC 20002 — open Mon–Sat 12–8 PM, Sun 12–7 PM. 21+ with valid ID; medical card required.`,
    breadcrumb: [["Home", "/"], ["Areas Served", "/areas-served"], [guide.name, `/dispensary/${guide.slug}`]],
  });

  const points = [
    `Curated premium flower, prerolls, vapes, edibles, and concentrates`,
    `Patient-first guidance from staff who live for the plant`,
    `A refined, welcoming space near Union Market in Northeast DC`,
    `Live Flowhub menu refreshed throughout the day`,
  ];

  return (
    <div data-testid="area-guide-page">
      <section className="relative flex min-h-[52vh] items-end overflow-hidden pb-16 pt-40">
        <div className="absolute inset-0">
          <img src={ASSETS.exteriorWide} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/40" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 lg:px-12">
          <Reveal>
            <Link to="/areas-served" data-testid="area-guide-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-gold">
              <ArrowLeft className="h-4 w-4" /> All Areas
            </Link>
          </Reveal>
          <Reveal delay={0.1}><Label className="mt-6 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {guide.region}</Label></Reveal>
          <Reveal delay={0.15}>
            <h1 className="mt-4 max-w-4xl font-display text-3xl font-light uppercase leading-tight tracking-[0.06em] text-white sm:text-4xl lg:text-5xl">
              Cannabis Dispensary Near {guide.name}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-16 md:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-[1.2fr_0.8fr] lg:px-12">
          <div>
            <Reveal>
              <p className="text-lg leading-relaxed text-white/70">{guide.blurb}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 leading-relaxed text-white/60">
                Looking for a trusted medical cannabis dispensary near {guide.name}? Higher Ground DC is a short trip
                away at {ADDRESS.line1}, {ADDRESS.line2}. We pair a premium, boutique experience with expert,
                patient-first guidance — so whether you're a longtime consumer or new to cannabis, you'll feel
                informed, supported, and at ease.
              </p>
            </Reveal>
            <ul className="mt-8 space-y-3">
              {points.map((p, i) => (
                <Reveal key={p} delay={0.06 * i}>
                  <li className="flex items-start gap-3 text-white/70"><Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold" /> {p}</li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href={LINKS.menu} target="_blank" rel="noopener noreferrer" data-testid="area-guide-shop" className="inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">
                  Shop the Menu <ArrowUpRight className="h-4 w-4" />
                </a>
                <a href={LINKS.google} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:border-gold hover:text-gold">
                  Get Directions
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-ink-2 p-8">
              <h2 className="font-display text-xl font-light uppercase tracking-[0.1em] text-white">Visit Higher Ground DC</h2>
              <div className="mt-6 space-y-4 text-sm text-white/60">
                <p className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 text-gold" /> {ADDRESS.line1}, {ADDRESS.line2}</p>
                <p><span className="text-white/40">Hours · </span>{HOURS}</p>
                <p><span className="text-white/40">Phone · </span>(202) 800-1135</p>
              </div>
              <div className="mt-8 border-t border-white/10 pt-6">
                <p className="text-xs uppercase tracking-[0.2em] text-gold">Nearby Guides</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {others.map((g) => (
                    <Link key={g.slug} to={`/dispensary/${g.slug}`} className="border border-white/10 px-3 py-1.5 text-xs text-white/60 transition-colors hover:border-gold/50 hover:text-white">
                      {g.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <MapBlock label={`Near ${guide.name}`} />
      <MedicalAccess />
    </div>
  );
}
